import { useQuery } from '@apollo/client';
import { Card, CardContent, FormControlLabel, FormGroup, Grid, Switch, useMediaQuery } from '@mui/material';
import * as Schema from 'generated/graphql/schema';
import { memoize } from 'lodash';
import { mean } from 'lodash';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import LiveDataButton from '@/components/buttons/live-data-button';
import LoadingOrError from '@/components/loading/loading-or-error';
import TimePicker from '@/components/pickers/time-picker';
import { useTimePicker } from '@/contexts/timepicker';
import { lineConsolidatedOEE } from '@/graphql/queries';
import { useTranslation } from '@/hooks';
import { updateDataHash } from '@/lib/hash';
import * as Types from '@/types';
import OEEKPICard from '@/views/tabs/oee/oee-kpi-card';
import OEETableCard from '@/views/tabs/oee/oee-table-card';
import OEEWaterfallCard from '@/views/tabs/oee/oee-waterfall-card';

const useStyles = makeStyles()((theme) => ({
  card: {
    width: '100%',
    minHeight: '70px',
  },
  loadingCardContent: {
    height: '200px',
  },
  flexContainer: {
    display: 'flex',
  },
  includeScrapButton: {
    marginLeft: theme.spacing(2),
  },
}));

interface Properties {
  lines: Array<NonNullable<Schema.ConsolidatedGroupsLinesQuery['user']['groups'][0]['lines'][0]>>;
}

const getWaterfallStatsFromStopData = (stopsData: Pick<Schema.LineStopStats, 'stopStats'>[]) => {
  return stopsData.reduce((stopStats, cur) => {
    stopStats.push(...cur.stopStats.stopCauseStats.filter(Types.isTruthy));
    return stopStats;
  }, [] as Schema.StopCauseStat[]);
};

const memoizedFallbackTo = memoize(
  (to: Date | null) => to || new Date(),
  (to: Date | null) => `${to?.getTime()}`,
);

const ConsolidatedOEELineGrid = ({ lines }: Properties) => {
  const [includeScrap, setIncludeScrap] = useState(true);
  const { classes, theme } = useStyles();
  const [t] = useTranslation();
  const [dataHash, setDataHash] = useState('');

  const { from, to, setTime } = useTimePicker();
  const fallbackTo = memoizedFallbackTo(to);
  const timeRange = { from, to: fallbackTo };
  const toggleScrap = () => {
    setIncludeScrap(!includeScrap);
    refetch();
  };

  const { data, loading, error, refetch } = useQuery<
    Schema.LineConsolidatedOeeQuery,
    Schema.LineConsolidatedOeeQueryVariables
  >(lineConsolidatedOEE, {
    ssr: false,
    partialRefetch: true,
    errorPolicy: 'all',
    onCompleted: updateDataHash(setDataHash),
    variables: {
      lineIds: lines.map((line) => line.id),
      time: Types.toISOString(timeRange),
      includeScrap,
    },
  });

  const oee = data?.consolidatedData?.oee || null;
  const { oee1, oee2, oee3, tcu } = oee || { oee1: 0, oee2: 0, oee3: 0, tcu: 0 };

  const stopsData = (data?.consolidatedData?.stopsData ?? []).filter(
    (item): item is NonNullable<typeof item> => !!item,
  );
  const stopStats = getWaterfallStatsFromStopData(stopsData);

  const smallDown = useMediaQuery(theme.breakpoints.down('xs'));

  const fullLoading = !!oee ? false : loading;
  const chartLoading = loading;
  const includeScrapButton = (
    <FormGroup>
      <FormControlLabel
        control={<Switch value="with-scrap" color="primary" checked={includeScrap} onClick={toggleScrap} />}
        label={t(['shared:includeScrap'], { defaultValue: 'Include scrap' })}
        disabled={loading}
      />
    </FormGroup>
  );

  const targets = lines.reduce<{ oee: number[]; oee2: number[]; oee3: number[]; tcu: number[] }>(
    (aggregatedTargets, line) => {
      const weeklyTargets = line.schedule?.weeklyTargets;
      if (!weeklyTargets) {
        return aggregatedTargets;
      }

      for (const [kpi, targetValue] of Object.entries(weeklyTargets) as Array<
        [keyof typeof weeklyTargets, number | null]
      >) {
        if (typeof targetValue === 'number') {
          aggregatedTargets[kpi].push(targetValue);
        }
      }

      return aggregatedTargets;
    },
    { oee: [], oee2: [], oee3: [], tcu: [] },
  );

  const oee1Target = targets.oee.length ? mean(targets.oee) : undefined;
  const oee2Target = targets.oee2.length ? mean(targets.oee2) : undefined;
  const oee3Target = targets.oee3.length ? mean(targets.oee3) : undefined;
  const tcuTarget = targets.tcu.length ? mean(targets.tcu) : undefined;

  return (
    <Grid container spacing={1} alignItems="stretch" justifyContent="flex-start">
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Card className={classes.card} elevation={1}>
          <CardContent>
            <>
              {smallDown ? (
                <div className={classes.flexContainer} style={{ justifyContent: 'space-between' }}>
                  {includeScrapButton}
                  <LiveDataButton loading={loading} />
                </div>
              ) : null}
              <div className={classes.flexContainer}>
                <TimePicker includeLargeRanges />
                {smallDown ? null : (
                  <>
                    {includeScrapButton}
                    <LiveDataButton loading={loading} />
                  </>
                )}
              </div>
            </>
          </CardContent>
        </Card>
      </Grid>
      {fullLoading || (error && !data) ? (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <Card elevation={1}>
            <CardContent classes={{ root: classes.loadingCardContent }}>
              <LoadingOrError loading={fullLoading} error={error} />
            </CardContent>
          </Card>
        </Grid>
      ) : (
        <>
          <LoadingOrError loading={false} error={error} data={data} />
          <OEEKPICard
            gaugeColor={theme.blackbird.stops.NO_ACT.color.normal}
            title={'TCU'}
            value={tcu}
            targetValue={tcuTarget}
          />
          <OEEKPICard
            gaugeColor={theme.blackbird.stops.NON_PROD.color.normal}
            title={
              <>
                OEE
                <sub>3</sub>
              </>
            }
            value={oee3}
            targetValue={oee3Target}
          />
          <OEEKPICard
            gaugeColor={theme.blackbird.stops.BATCH_NON_PROD.color.normal}
            title={
              <>
                OEE
                <sub>2</sub>
              </>
            }
            value={oee2}
            targetValue={oee2Target}
          />
          <OEEKPICard
            gaugeColor={theme.blackbird.stops.DOWNTIME.color.normal}
            title={
              <>
                OEE
                <sub>1</sub>
              </>
            }
            value={oee1}
            targetValue={oee1Target}
          />
        </>
      )}

      <OEEWaterfallCard
        data={{ timeRange: Types.toISOString(timeRange), oee, stopStats }}
        dataHash={dataHash}
        refetch={setTime}
        loading={chartLoading}
      />
      <OEETableCard data={{ timeRange: Types.toISOString(timeRange), oee, stopStats }} />
    </Grid>
  );
};

export default ConsolidatedOEELineGrid;
